import { Calendar, TrendingDown, TrendingUp, Flame, Zap, Sparkles } from 'lucide-react';
import InlinePopover from '@/components/InlinePopover';
import { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * WatchlistRadarChips
 * --------------------
 * Renders compact "why on radar" chips next to a stock's badges on
 * the Watchlist view ONLY. Pre-existing chips already cover hike/cut
 * (DividendChangeBadge), quality (QualityBadge), tier (TierBadge),
 * REIT/BDC/CEF/ADR. This component fills the remaining gaps:
 *   • Ex-div within 7 days — hot upcoming income event.
 *   • Near 52-week low — possible buying opportunity.
 *   • Near 52-week high — premium valuation; consider waiting.
 *   • High Yield (≥ 8%) — dividend hunter's hot pick.
 *   • Trending — community signal pulled from /api/watchlist/trending.
 *
 * All chips are pure render — no per-row fetches. The trending list
 * is fetched ONCE by the watchlist view and passed in as a Set of
 * tickers via the `trendingTickers` prop.
 */

const ONE_DAY_MS = 86_400_000;
const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

// ---- Individual chips -------------------------------------------------------

function ExDivSoonChip({ stock }) {
  const exDate = stock?.ex_dividend_date;
  if (!exDate) return null;
  const ex = new Date(exDate);
  if (isNaN(ex.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffDays = Math.round((ex.getTime() - today.getTime()) / ONE_DAY_MS);
  if (diffDays < 0 || diffDays > 7) return null;
  const label =
    diffDays === 0 ? 'Ex-div today'
    : diffDays === 1 ? 'Ex-div tomorrow'
    : `Ex-div in ${diffDays}d`;
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-300 border border-sky-300/60 dark:border-sky-700/60"
      title={`Ex-dividend date ${exDate} — ${diffDays === 0 ? 'today' : diffDays + ' day(s) away'}. Buy before this date to receive the next dividend.`}
      data-testid={`radar-exdiv-${stock.ticker}`}
    >
      <Calendar className="w-2.5 h-2.5" />
      {label}
    </span>
  );
}

function Near52WeekLowChip({ stock }) {
  const low = Number(stock?.fifty_two_week_low);
  const price = Number(stock?.price);
  if (!Number.isFinite(low) || !Number.isFinite(price) || low <= 0 || price <= 0) return null;
  const pctAbove = (price - low) / low;
  if (pctAbove < 0 || pctAbove > 0.05) return null;
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-violet-100 text-violet-800 dark:bg-violet-950/60 dark:text-violet-300 border border-violet-300/60 dark:border-violet-700/60"
      title={`Within 5% of 52-week low ($${low.toFixed(2)}). Current price is ${(pctAbove * 100).toFixed(1)}% above the low — possible buying opportunity if fundamentals are intact.`}
      data-testid={`radar-near-52w-low-${stock.ticker}`}
    >
      <TrendingDown className="w-2.5 h-2.5" />
      Near 52w low
    </span>
  );
}

function Near52WeekHighChip({ stock }) {
  const high = Number(stock?.fifty_two_week_high);
  const price = Number(stock?.price);
  if (!Number.isFinite(high) || !Number.isFinite(price) || high <= 0 || price <= 0) return null;
  const pctBelow = (high - price) / high;
  // Within 3% of 52-week high → "premium valuation" warning
  if (pctBelow < 0 || pctBelow > 0.03) return null;
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200 border border-amber-300/60 dark:border-amber-700/60"
      title={`Within 3% of 52-week high ($${high.toFixed(2)}). Current price is ${((1 - pctBelow) * 100).toFixed(1)}% of the high — at a premium. Consider waiting for a pullback or scaling in.`}
      data-testid={`radar-near-52w-high-${stock.ticker}`}
    >
      <TrendingUp className="w-2.5 h-2.5" />
      Near 52w high
    </span>
  );
}

function HighYieldChip({ stock }) {
  // Skip when other badges already convey high-yield risk so we don't
  // double-flag the same fact (leveraged, futures-based, and the
  // separate YieldTrapBadge already do their own warnings).
  if (stock?.is_leveraged || stock?.uses_futures) return null;
  const y = Number(stock?.dividend_yield);
  if (!Number.isFinite(y) || y < 8) return null;
  // 8-15% = High Yield (sky/emerald); >15% = Very High Yield (rose).
  const veryHigh = y >= 15;
  const cls = veryHigh
    ? 'bg-rose-100 text-rose-800 dark:bg-rose-950/60 dark:text-rose-300 border-rose-300/60 dark:border-rose-700/60'
    : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-950/60 dark:text-emerald-300 border-emerald-300/60 dark:border-emerald-700/60';
  const label = veryHigh ? 'Very high yield' : 'High yield';
  return (
    <span
      className={`inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide border ${cls}`}
      title={`${y.toFixed(2)}% yield. ${veryHigh ? 'Very high yields (>15%) often signal distribution risk or aggressive option-income strategies — verify sustainability before buying.' : 'High dividend yield (8-15%) — common for BDCs, REITs, and covered-call ETFs.'}`}
      data-testid={`radar-high-yield-${stock.ticker}`}
    >
      <Zap className="w-2.5 h-2.5" />
      {label}
    </span>
  );
}

function TrendingChip({ stock, trendingTickers }) {
  if (!trendingTickers || !trendingTickers.has || !trendingTickers.has(stock.ticker)) return null;
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-orange-100 text-orange-900 dark:bg-orange-950/60 dark:text-orange-200 border border-orange-300/60 dark:border-orange-700/60"
      title="Trending on the platform — recently added to many users' watchlists."
      data-testid={`radar-trending-${stock.ticker}`}
    >
      <Flame className="w-2.5 h-2.5" />
      Trending
    </span>
  );
}

function MonthlyIncomeChip({ stock }) {
  const y = Number(stock?.dividend_yield);
  if (!Number.isFinite(y) || y <= 0) return null;
  // Compact chip: "$1k → $X/mo" — the killer "income impact" tease
  // from the brainstorm. Hover gets the full breakdown.
  const monthlyPer1k = (1000 * (y / 100)) / 12;
  if (monthlyPer1k < 0.01) return null;
  const fmt = (v) => v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const rows = [
    ['$1,000', monthlyPer1k],
    ['$10,000', monthlyPer1k * 10],
    ['$100,000', monthlyPer1k * 100],
  ];
  return (
    <InlinePopover
      className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-teal-100 text-teal-900 dark:bg-teal-950/60 dark:text-teal-200 border border-teal-300/60 dark:border-teal-700/60 cursor-pointer hover:bg-teal-200 dark:hover:bg-teal-900/70 transition-colors"
      title={`At current ${y.toFixed(2)}% yield:\n• $1,000 invested → $${monthlyPer1k.toFixed(2)}/mo\n• $10,000 → $${(monthlyPer1k * 10).toFixed(2)}/mo\n• $100,000 → $${(monthlyPer1k * 100).toFixed(2)}/mo\n\nReal cash flow — turns abstract yield into actual monthly income.`}
      testId={`radar-income-${stock.ticker}`}
      maxWidth={300}
      bubbleClassName="bg-white text-black border-neutral-300"
      render={() => (
        <div className="text-left text-black" data-testid={`radar-income-breakdown-${stock.ticker}`}>
          <p className="text-[11px] font-bold mb-1.5">
            {stock.ticker} · Income Impact at {y.toFixed(2)}% yield
          </p>
          <table className="w-full text-[11px] font-mono">
            <thead>
              <tr className="text-[9px] uppercase tracking-wider text-neutral-500">
                <th className="text-left font-semibold pb-0.5">Invested</th>
                <th className="text-right font-semibold pb-0.5">Monthly</th>
                <th className="text-right font-semibold pb-0.5 pl-2">Yearly</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(([invested, mo]) => (
                <tr key={invested} className="border-t border-neutral-200">
                  <td className="py-1 pr-2 text-neutral-700 whitespace-nowrap">{invested}</td>
                  <td className="py-1 text-right font-bold">${fmt(mo)}/mo</td>
                  <td className="py-1 pl-2 text-right text-neutral-600">${fmt(mo * 12)}/yr</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-1.5 text-[10px] leading-snug text-neutral-600">
            Real cash flow — turns abstract yield into actual monthly income. Based on today&apos;s yield; not a guarantee.
          </p>
        </div>
      )}
    >
      <Sparkles className="w-2.5 h-2.5" />
      ${monthlyPer1k.toFixed(2)}/mo per $1k
    </InlinePopover>
  );
}

function YocProjectionChip({ stock }) {
  const y = Number(stock?.dividend_yield);
  const cagr = Number(stock?.five_year_dividend_cagr ?? stock?.three_year_dividend_cagr);
  if (!Number.isFinite(y) || y <= 0) return null;
  if (!Number.isFinite(cagr) || cagr <= 0) return null;
  // 5-year YoC projection = current_yield × (1 + cagr)^5
  // Skip when growth is exotic (>50%/yr) — usually data noise.
  if (cagr > 50) return null;
  const projected = y * Math.pow(1 + cagr / 100, 5);
  // Only render when there's real upside (projection ≥ 1.25× current yield).
  if (projected / y < 1.25) return null;
  return (
    <span
      className="inline-flex items-center gap-0.5 px-1.5 py-0 rounded text-[9px] font-bold uppercase tracking-wide bg-fuchsia-100 text-fuchsia-900 dark:bg-fuchsia-950/60 dark:text-fuchsia-200 border border-fuchsia-300/60 dark:border-fuchsia-700/60"
      title={`5-year Yield-on-Cost projection at the current ${cagr.toFixed(2)}% dividend growth rate:\n  ${y.toFixed(2)}% × (1 + ${cagr.toFixed(2)}%)⁵ = ${projected.toFixed(2)}%\n\nIf you buy today and div growth holds, your effective yield in 5 years on TODAY's invested dollars would be ${projected.toFixed(2)}%.`}
      data-testid={`radar-yoc5y-${stock.ticker}`}
    >
      <TrendingUp className="w-2.5 h-2.5" />
      5Y YoC ~{projected.toFixed(1)}%
    </span>
  );
}

// ---- Top-level renderer -----------------------------------------------------

export default function WatchlistRadarChips({ stock, trendingTickers }) {
  if (!stock) return null;
  return (
    <>
      <TrendingChip stock={stock} trendingTickers={trendingTickers} />
      <ExDivSoonChip stock={stock} />
      <HighYieldChip stock={stock} />
      <Near52WeekLowChip stock={stock} />
      <Near52WeekHighChip stock={stock} />
      <MonthlyIncomeChip stock={stock} />
      <YocProjectionChip stock={stock} />
    </>
  );
}

/**
 * useTrendingWatchlistTickers
 * ----------------------------
 * Small hook that fetches the trending watchlist tickers ONCE on
 * mount (or `enabled` flips to true) and returns them as a Set.
 * Auto-refreshes every 5 minutes — trending is a slow signal.
 * Caller (Dashboard.js) only enables it on the watchlist view.
 */
export function useTrendingWatchlistTickers(enabled) {
  const [set, setSet] = useState(() => new Set());
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    const fetchOnce = async () => {
      try {
        const { data } = await axios.get(`${API}/watchlist/trending`, { withCredentials: true });
        if (cancelled) return;
        const tickers = Array.isArray(data?.tickers) ? data.tickers : [];
        setSet(new Set(tickers.map((t) => String(t).toUpperCase())));
      } catch {
        // Silent — trending is a nice-to-have, not critical.
      }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 5 * 60 * 1000);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled]);
  return set;
}
